import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Sun, Moon, Zap } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/commodities", label: "Engineering Commodities" },
  { to: "/industries", label: "Industries" },
  { to: "/services", label: "Services" },
  { to: "/supplier-registration", label: "Suppliers" },
  { to: "/buyer-registration", label: "Buyers" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggle } = useTheme();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 w-full z-50 transition-shadow duration-300 border-b border-border bg-background/90 backdrop-blur-xl ${
        scrolled ? "shadow-[0_2px_16px_-6px_rgba(11,17,32,0.25)]" : ""
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 group shrink-0">
          <img src="/brand-logo.png" alt="SourceX Global logo" className="h-10 w-10 md:h-11 md:w-11 rounded-full object-cover shadow-sm transition-transform group-hover:scale-105" />
          <span className="font-display font-black text-foreground text-lg tracking-tight leading-none">
            SourceX<span className="text-brand-orange"> Global</span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-5">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `font-mono uppercase text-[10.5px] tracking-[0.14em] whitespace-nowrap transition-colors hover:text-brand-orange ${
                  isActive ? "text-brand-orange" : "text-muted-foreground"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2.5">
          <Link
            to="/fasttrack"
            data-testid="nav-fasttrack-btn"
            className="fasttrack-btn hidden md:inline-flex items-center gap-2 text-white px-4 py-2.5 font-mono uppercase text-[11px] tracking-[0.16em] transition-colors"
          >
            <Zap size={14} className="fasttrack-blink" fill="white" /> FastTrack
          </Link>
          <button
            onClick={toggle}
            data-testid="theme-toggle"
            aria-label="Toggle theme"
            className="h-10 w-10 grid place-items-center border border-border text-foreground hover:border-brand-orange hover:text-brand-orange transition-colors"
          >
            {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
          </button>
          <Link
            to="/quote"
            data-testid="nav-quote-btn"
            className="hidden sm:inline-flex items-center gap-2 bg-brand-orange text-white px-5 py-2.5 font-mono uppercase text-[11px] tracking-[0.18em] hover:bg-brand-orangeHover transition-colors group"
          >
            Request a Quote
            <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
          <button
            data-testid="mobile-menu-btn"
            className="xl:hidden text-foreground p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="xl:hidden bg-background border-t border-border max-h-[80vh] overflow-y-auto">
          <div className="px-5 py-4 flex flex-col">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className="py-3 font-mono uppercase text-xs tracking-[0.18em] text-muted-foreground border-b border-border"
              >
                {l.label}
              </NavLink>
            ))}
            <NavLink to="/request-quote" className="py-3 font-mono uppercase text-xs tracking-[0.18em] text-muted-foreground border-b border-border">
              Buyer RFQ
            </NavLink>
            <Link to="/quote" className="mt-4 bg-brand-orange text-white text-center py-3 font-mono uppercase text-xs tracking-[0.18em]">
              Request a Quote
            </Link>
            <Link to="/fasttrack" className="fasttrack-btn mt-3 text-white text-center py-3 font-mono uppercase text-xs tracking-[0.18em] flex items-center justify-center gap-2">
              <Zap size={14} fill="white" /> FastTrack Sourcing
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
